import { useState } from "react";
import { X } from "lucide-react";
import { useSite } from "@/context/SiteContext";

const categoryLabels = {
  tonte: "Tonte de gazon", 
  entretien: "Entretien de terrain",
  piscine: "Contour de piscine", 
  vitres: "Lavage de vitres et gouttières",
};
const categoryOrder = ["tonte", "entretien", "piscine", "vitres"];

// Fallback SVG for broken images
const FALLBACK_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Crect fill='%23f5f5f0' width='400' height='400'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%232D5A3D' font-size='14' font-family='sans-serif'%3EImage non disponible%3C/text%3E%3C/svg%3E";

export const GallerySection = () => {
  const { gallery } = useSite();
  const [selected, setSelected] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});

  const handleImageLoad = (id) => {
    setLoadedImages(prev => ({ ...prev, [id]: true }));
  };

  const handleImageError = (e, id) => {
    e.target.onerror = null;
    e.target.src = FALLBACK_IMAGE;
    setLoadedImages(prev => ({ ...prev, [id]: 'error' }));
  };

  const grouped = categoryOrder
    .map((cat) => ({ 
      category: cat, 
      label: categoryLabels[cat], 
      items: gallery.filter((img) => img.category === cat) 
    }))
    .filter((g) => g.items.length > 0);

  const uncategorized = gallery.filter((img) => !img.category || !categoryOrder.includes(img.category));
  if (uncategorized.length > 0) {
    grouped.push({ category: "other", label: "Autres réalisations", items: uncategorized });
  }

  if (gallery.length === 0) return null;

  return (
    <section id="galerie" className="bg-[#F5F5F0] py-16 md:py-24" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-testid="gallery-title">Nos réalisations</h2>
          <p className="text-base lg:text-lg text-gray-600 max-w-2xl mx-auto">Quelques exemples de nos travaux d'entretien extérieur.</p>
        </div>
        <div className="space-y-10">
          {grouped.map((g) => (
            <div key={g.category} data-testid={`gallery-group-${g.category}`}>
              <h3 className="text-lg font-semibold text-[#2D5A3D] mb-4">{g.label}</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {g.items.map((img) => (
                  <button 
                    key={img.id} 
                    onClick={() => setSelected(img)} 
                    className="relative aspect-square rounded-lg overflow-hidden group cursor-pointer bg-gray-200" 
                    data-testid={`gallery-image-${img.id}`}
                  >
                    {/* Loading placeholder */}
                    {!loadedImages[img.id] && (
                      <div className="absolute inset-0 bg-gray-200 animate-pulse" />
                    )}
                    <img 
                      src={img.url} 
                      alt={img.caption || `Réalisation ${categoryLabels[img.category] || 'entretien extérieur'}`}
                      className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${loadedImages[img.id] ? 'opacity-100' : 'opacity-0'}`}
                      width={400}
                      height={400}
                      loading="lazy"
                      decoding="async"
                      onLoad={() => handleImageLoad(img.id)}
                      onError={(e) => handleImageError(e, img.id)}
                    />
                    {img.caption && (
                      <div className="absolute inset-x-0 bottom-0 bg-black/50 text-white text-xs p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        {img.caption}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Lightbox */}
      {selected && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" 
          onClick={() => setSelected(null)} 
          data-testid="gallery-lightbox"
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={() => setSelected(null)} 
              className="absolute -top-10 right-0 text-white hover:text-gray-300 p-2" 
              data-testid="lightbox-close"
              aria-label="Fermer"
            >
              <X className="w-6 h-6" />
            </button>
            <img 
              src={selected.url} 
              alt={selected.caption || "Réalisation Les Entretiens Élites"} 
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = FALLBACK_IMAGE;
              }}
            />
            {selected.caption && (
              <p className="text-white text-center mt-3 text-sm">{selected.caption}</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
};
